import { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import QuestionBubble from "../components/question/QuestionBubble.tsx";
import QuestionPageLayout from "../components/question/QuestionPageLayout.tsx";
import { useRandomCharacterImage } from "../hooks/useRandomCharacterImage.ts";

const QuestionWordTranslationPage = ({
  hearts,
  onNext,
  onBack,
  progress,
  isLast,
  onValidate,
  onAnswer,
  onIncorrectAnswer,
  darkMode = false,
  questionConfig,
  vocabularyBank,
  lessonData,
}: {
  hearts: any;
  onNext: any;
  onBack: any;
  progress: any;
  isLast: any;
  onValidate: any;
  onAnswer: any;
  onIncorrectAnswer: any;
  darkMode?: boolean;
  questionConfig?: any;
  vocabularyBank?: any[];
  lessonData?: any;
}) => {
  // Create stable dependency values
  const characterImg = useRandomCharacterImage([
    questionConfig?.correctAnswer,
    questionConfig?.wordBank?.join(","),
  ]);
  // Use lesson-specific vocabulary bank or fallback to default
  const wordBank = vocabularyBank || [];
  const wordDict = Object.fromEntries(wordBank.map((w: any) => [w.id, w]));

  console.log("🔤 WordTranslation Debug:");
  console.log("📋 questionConfig:", questionConfig);
  console.log("📚 vocabularyBank length:", vocabularyBank?.length || 0);
  console.log("🔍 wordDict keys:", Object.keys(wordDict));

  // Use question config if available, otherwise fallback
  const quest_batak = questionConfig?.wordBank || [105]; // Use first word as fallback
  const wordsForQuestion = quest_batak
    .map((id: number) => {
      const word = wordDict[id];
      if (!word) {
        console.warn(`⚠️ Word ID ${id} not found in vocabulary bank`);
      }
      return word;
    })
    .filter(Boolean);

  const optionIds =
    questionConfig?.options?.map((opt: any) => opt.id) ||
    quest_batak.slice(0, 4);
  console.log("🎯 optionIds:", optionIds);
  console.log("🔤 wordsForQuestion:", questionConfig?.options);

  // const options = optionIds
  //   .map((id: number) => {
  //     const word = wordBank.find((w: any) => w.id === id);
  //     if (!word) {
  //       console.warn(`⚠️ Option word ID ${id} not found in vocabulary bank`);
  //       return null;
  //     }
  //     return word;
  //   })
  //   .filter(Boolean)
  //   .map((w: any) => ({
  //     id: w.id,
  //     label: w.indonesia,
  //     image: w.image
  //   }));
  const options = (questionConfig.options || []).map((opt: any) => ({
    id: opt.id,
    label: opt.text || wordDict[opt.id]?.indonesia,
    image: opt.image,
  }));

  console.log("✅ options:", options);

  const correctAnswer = questionConfig?.correctAnswer || quest_batak[0];
  const correctIdx = options.findIndex((opt: any) => opt.id === correctAnswer);

  console.log("🎯 correctAnswer ID:", correctAnswer);
  console.log("🎯 correctIdx:", correctIdx);

  // Safety check: if correctIdx is -1, it means the correct answer is not in options
  if (correctIdx === -1) {
    console.error("❌ Correct answer not found in options!");
    console.error(
      "📋 Available option IDs:",
      options.map((opt) => opt.id)
    );
    console.error("🎯 Looking for ID:", correctAnswer);
  }

  const [showPopup, setShowPopup] = useState<string | false>(false);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [checked, setChecked] = useState(false);

  // Create stable dependency values
  const questionId = questionConfig?.correctAnswer;
  const wordBankStr = questionConfig?.wordBank?.join(",");

  // Reset state when question changes
  useEffect(() => {
    console.log("🔄 Question changed, resetting state");
    setSelectedIdx(null);
    setChecked(false);
    setShowPopup(false);
  }, [questionId, wordBankStr]);

  console.log(
    "🎮 Current state - selectedIdx:",
    selectedIdx,
    "checked:",
    checked
  );

  // Determine feedback state for each option
  const getOptionClass = (idx: number) => {
    if (checked) {
      if (idx === selectedIdx && idx === correctIdx) {
        return darkMode
          ? "border-lime-400 bg-lime-800/90"
          : "border-lime-500 bg-lime-100";
      }
      if (idx === selectedIdx && idx !== correctIdx) {
        return darkMode
          ? "border-red-400 bg-red-800/90"
          : "border-red-500 bg-red-100";
      }
      if (idx === correctIdx) {
        return darkMode
          ? "border-lime-400 bg-lime-800/90"
          : "border-lime-500 bg-lime-100";
      }
    } else if (idx === selectedIdx) {
      return darkMode
        ? "border-blue-400 bg-blue-900"
        : "border-blue-500 bg-blue-50";
    }
    return darkMode
      ? "border-gray-500 bg-gray-800"
      : "border-gray-300 bg-white";
  };

  // Call onValidate after checking
  const handleCheck = () => {
    const isCorrect = selectedIdx === correctIdx;
    if (onAnswer) onAnswer(isCorrect ? 10 : 0, isCorrect ? 1 : 0, 1); // (xp, correct, total)

    // Reduce heart if answer is incorrect
    if (!isCorrect && onIncorrectAnswer) {
      onIncorrectAnswer();
    }

    if (onValidate) onValidate();
    setChecked(true);
  };

  const isMobile = useMediaQuery({ maxWidth: 767 });
  return (
    <QuestionPageLayout
      hearts={hearts}
      onBack={onBack}
      progress={progress}
      darkMode={darkMode}
    >
      <h1
        className={`md:text-2xl text-lg font-bold text-center md:mb-8 mb-4 ${
          darkMode ? "text-gray-100" : "text-gray-800"
        }`}
        style={{ fontFamily: "Feather Bold" }}
      >
        Pilih terjemahan yang tepat!
      </h1>

      <div className="flex flex-col items-center md:mb-10 mb-5">
        <div
          className={`flex items-center justify-center ${
            isMobile ? "gap-3 mb-5" : "gap-6 mb-10"
          }`}
        >
          <img
            src={characterImg}
            alt="Characters"
            className={
              isMobile ? "w-20 h-20 object-contain" : "w-32 h-32 object-contain"
            }
          />
          <QuestionBubble
            words={wordsForQuestion}
            showPopup={showPopup}
            setShowPopup={setShowPopup}
            darkMode={darkMode}
          />
        </div>
      </div>

      <div
        className={`grid ${
          isMobile ? "grid-cols-2" : "grid-cols-4"
        } gap-6 w-full max-w-4xl mx-auto mb-8`}
        style={{ fontFamily: "DIN Next Rounded" }}
      >
        {options.map((option, idx) => (
          <button
            key={option.label}
            type="button"
            onClick={() => {
              if (!checked) setSelectedIdx(idx);
            }}
            className={`
              flex flex-col items-center rounded-xl border shadow-md transition
              ${
                isMobile ? "p-2 max-w-[180px]" : "p-4 max-w-xs"
              } w-full cursor-pointer
              ${getOptionClass(idx)}
              ${checked ? "cursor-default" : "hover:shadow-lg"}
              ${
                selectedIdx === idx && !checked
                  ? darkMode
                    ? "ring-2 ring-blue-300"
                    : "ring-2 ring-blue-400"
                  : ""
              }
              `}
            disabled={checked}
            style={{
              opacity: 1,
            }}
          >
            <img
              src={option.image || "/assets/aksa-1.png"}
              alt={option.label}
              className={
                isMobile
                  ? "w-14 h-14 object-contain mb-1"
                  : "w-24 h-24 object-contain mb-2"
              }
            />
            <div
              className={`text-center font-medium ${
                isMobile ? "text-sm" : "text-lg"
              } ${darkMode ? "text-gray-100" : "text-gray-800"}`}
              style={{ fontFamily: "DIN Next Rounded" }}
            >
              {option.label}
            </div>
          </button>
        ))}
      </div>

      <div className="flex justify-center">
        {!checked ? (
          <button
            type="button"
            className={`md:px-8 px-5 md:py-3 py-2 rounded-lg font-bold md:text-base text-sm transition-colors
              ${
                selectedIdx === null
                  ? darkMode
                    ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                    : "bg-gray-400 text-white cursor-not-allowed"
                  : darkMode
                  ? "bg-blue-700 hover:bg-blue-800 text-white"
                  : "bg-blue-500 hover:bg-blue-600 text-white"
              }
            `}
            style={{ fontFamily: "DIN Next Rounded" }}
            disabled={selectedIdx === null}
            onClick={handleCheck}
          >
            Periksa
          </button>
        ) : (
          <button
            onClick={onNext}
            className={`md:px-8 px-5 md:py-3 py-2 rounded-lg font-bold md:text-base text-sm transition-colors text-white ${
              darkMode
                ? "bg-lime-700 hover:bg-lime-800"
                : "bg-lime-500 hover:bg-lime-600"
            }`}
            style={{ fontFamily: "DIN Next Rounded" }}
          >
            {isLast ? "Selesai" : "Lanjutkan"}
          </button>
        )}
      </div>
    </QuestionPageLayout>
  );
};

export default QuestionWordTranslationPage;
